import type { ChatWidgetProps, ChatWidgetSettings, Theme } from '../types/chat';
import { DEFAULT_THEME } from '../constants';
import { CONFIG_KEYS, type ConfigKeySpec } from './aliases';
import type { WidgetConfig } from './schema';

const EMPTY_OBJECT = Object.freeze({}) as Record<string, never>;

/** `undefined`, `null` and `''` all mean "not set" and defer to the next source. */
export function isPresent(value: unknown): boolean {
  return value !== undefined && value !== null && value !== '';
}

export function isPlainObject(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

export function getPath(source: unknown, path: string): unknown {
  let current: unknown = source;
  for (const segment of path.split('.')) {
    if (!isPlainObject(current)) return undefined;
    current = current[segment];
  }
  return current;
}

export function firstPresent(source: unknown, paths: string[] | undefined): unknown {
  if (!source || !paths) return undefined;
  for (const path of paths) {
    const value = getPath(source, path);
    if (isPresent(value)) return value;
  }
  return undefined;
}

/**
 * Resolves one canonical key: inline prop > inline `settings` > remote settings > fallback.
 * `merge` keys shallow-merge every object source instead (remote < inline settings < prop).
 */
export function resolveConfigValue(
  spec: ConfigKeySpec,
  props: ChatWidgetProps,
  remoteSettings: ChatWidgetSettings | null | undefined
): unknown {
  const fromProp = firstPresent(props, spec.props);
  const fromInline = firstPresent(props.settings, spec.settings);
  const fromRemote = firstPresent(remoteSettings, spec.settings);
  if (spec.merge) {
    const parts = [fromRemote, fromInline, fromProp].filter(isPlainObject);
    if (parts.length === 0) return spec.fallback;
    return Object.assign({}, ...parts);
  }
  if (isPresent(fromProp)) return fromProp;
  if (isPresent(fromInline)) return fromInline;
  if (isPresent(fromRemote)) return fromRemote;
  return spec.fallback;
}

function setPath(target: Record<string, unknown>, path: string, value: unknown): void {
  const segments = path.split('.');
  let current = target;
  for (let i = 0; i < segments.length - 1; i += 1) {
    const segment = segments[i];
    if (!isPlainObject(current[segment])) current[segment] = {};
    current = current[segment] as Record<string, unknown>;
  }
  current[segments[segments.length - 1]] = value;
}

function normalizeTranslations(raw: unknown): Record<string, string> {
  if (!isPlainObject(raw)) return EMPTY_OBJECT;
  const entries = Object.entries(raw).filter(
    ([, value]) => typeof value === 'string' && value.trim().length > 0
  );
  return entries.length ? (Object.fromEntries(entries) as Record<string, string>) : EMPTY_OBJECT;
}

/**
 * Builds the canonical `WidgetConfig` from the legacy prop surface.
 *
 * Behaviour notes (see WIDGET-CONFIGURATION.md, "Configuration model"):
 * - `null` and `''` inline values defer to `settings` / remote / default.
 * - `analytics.enabled` defaults to `false`; the legacy inference from
 *   `analyticsBaseUrl` is kept only when no source sets it explicitly and is
 *   reported through `analytics.enabledSource === 'inferred'`.
 */
export function normalizeWidgetConfig(
  props: ChatWidgetProps,
  remoteSettings: ChatWidgetSettings | null | undefined
): WidgetConfig {
  const draft: Record<string, unknown> = {};
  for (const spec of CONFIG_KEYS) {
    setPath(draft, spec.key, resolveConfigValue(spec, props, remoteSettings));
  }
  const config = draft as unknown as WidgetConfig;

  // Derived / normalized values
  const rawTheme = config.branding.theme as unknown;
  config.branding.theme = {
    ...DEFAULT_THEME,
    ...(isPlainObject(rawTheme) ? (rawTheme as Partial<Theme>) : {}),
  };
  config.i18n.translations = normalizeTranslations(config.i18n.translations);
  if (!isPlainObject(config.behavior.customFields)) config.behavior.customFields = EMPTY_OBJECT;
  if (!isPlainObject(config.behavior.records)) {
    config.behavior.records = EMPTY_OBJECT as WidgetConfig['behavior']['records'];
  }
  if (!isPresent(config.analytics.apiKey)) config.analytics.apiKey = config.security.apiKey;

  const telemetryEndpoint = config.telemetry.endpoint as string | null;
  const apiBaseUrl = config.endpoints.apiBaseUrl;
  config.telemetry.endpoint =
    telemetryEndpoint ||
    (apiBaseUrl ? `${apiBaseUrl.replace(/\/$/, '')}/telemetry` : '/api/response/telemetry');

  // analytics.enabled: an explicit value from any source wins; otherwise the default is
  // false. The legacy inference ("a base URL means tracking is on") is kept only for the
  // case no source sets it, so existing YSE/NMM deployments keep tracking; the hook logs
  // it once in development via `enabledSource === 'inferred'`.
  const explicitEnabled = config.analytics.enabled as unknown;
  if (explicitEnabled !== undefined) {
    config.analytics.enabled = !!explicitEnabled;
    config.analytics.enabledSource = 'explicit';
  } else if (config.analytics.baseUrl) {
    config.analytics.enabled = true;
    config.analytics.enabledSource = 'inferred';
  } else {
    config.analytics.enabled = false;
    config.analytics.enabledSource = 'default';
  }

  return config;
}
